import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspacePath } from './workspaceRoutes';

export interface OrgChartNavigationTarget {
  pid: string;
  requestId: number;
}

interface OrgChartNavigationState {
  target: OrgChartNavigationTarget | null;
  navigateToPerson: (pid: string) => void;
  clearTarget: (requestId: number) => void;
}

const Ctx = createContext<OrgChartNavigationState | null>(null);

export function personCardDomId(pid: string): string {
  return `person-card-${pid}`;
}

export function OrgChartNavigationProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const workspaceTo = useWorkspacePath();
  const [target, setTarget] = useState<OrgChartNavigationTarget | null>(null);

  const navigateToPerson = useCallback(
    (pid: string) => {
      setTarget((cur) => ({ pid, requestId: (cur?.requestId ?? 0) + 1 }));
      navigate(workspaceTo());
    },
    [navigate, workspaceTo],
  );

  const clearTarget = useCallback((requestId: number) => {
    setTarget((cur) => (cur?.requestId === requestId ? null : cur));
  }, []);

  const value = useMemo(
    () => ({ target, navigateToPerson, clearTarget }),
    [target, navigateToPerson, clearTarget],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOrgChartNavigation(): OrgChartNavigationState {
  const value = useContext(Ctx);
  if (!value) throw new Error('useOrgChartNavigation must be used inside OrgChartNavigationProvider');
  return value;
}
